import React, { useEffect, useState, useRef } from "react";
import styles from "./AppRegistration.module.css";
import "../../../styles/components.css";
import { fetchAuthSession } from "aws-amplify/auth";
import { FaSpinner } from "react-icons/fa";

function AppRegistration() {
  const [platform, setPlatform] = useState("Apple");
  const [appIdInput, setAppIdInput] = useState("");
  const [orgIdInput, setOrgIdInput] = useState("");
  const [certificateShaInput, setCertificateShaInput] = useState("");
  // DevRM fields
  const [appShortDescription, setAppShortDescription] = useState("");
  const [applicationType, setApplicationType] = useState("");
  const [usageDomain, setUsageDomain] = useState("");
  const [registerLoading, setRegisterLoading] = useState(false);
  const [registrations, setRegistrations] = useState([]);
  const [showRegisterForm, setShowRegisterForm] = useState(false);
  const [loadingRegistrations, setLoadingRegistrations] = useState(true);

  const didFetchRegistrations = useRef(false);

  // Sanitizers for app registration fields
  // Android package names: letters, digits, underscores, periods (e.g., com.example.my_app)
  // iOS bundle IDs: letters, digits, hyphens, periods (e.g., com.example.my-app)
  const sanitizeAppId = (val) => val.trim().replace(/[^0-9a-zA-Z\-\.\_]/g, "");
  const sanitizeOrgId = (val) => val.trim().replace(/[^0-9a-zA-Z]/g, ""); // Apple Team ID: alphanumeric only
  const sanitizeSha = (val) => val.trim().replace(/[^0-9a-fA-F\:]/g, ""); // SHA256 fingerprint: hex digits and colons

  // Fetch registrations on component mount
  useEffect(() => {
    if (didFetchRegistrations.current) return; // already ran once, stop here
    didFetchRegistrations.current = true;
    fetchRegistrations();
  }, []);

  async function deleteAppId(entryId) {
    const confirmed = window.confirm(
      "Are you sure you want to delete this App ID? This action cannot be undone."
    );
    if (!confirmed) return;
    try {
      // 1) grab your Cognito token
      const session = await fetchAuthSession();
      const jwt = session?.tokens?.accessToken;

      if (!jwt) {
        console.error('No JWT token available for deleting app ID');
        return;
      }

      // 2) build the URL
      const url = `${process.env.REACT_APP_USAGE_API}${process.env.REACT_APP_APP_ID}`;

      // 3) fire the DELETE
      const res = await fetch(url, {
        method: "DELETE",
        mode: "cors",
        headers: {
          Authorization: `Bearer ${jwt}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ id: entryId }),
      });

      const json = await res.json();
      if (res.ok && json?.response?.resultStatus === "SUCCESS") {
        // 4) refresh the list
        fetchRegistrations();
      } else {
        console.error("Failed to delete app ID:", json);
      }
    } catch (err) {
      console.error("Error deleting app ID:", err);
    }
  }

  async function downloadPlist(appId) {
    try {
      const session = await fetchAuthSession();
      const jwt = session?.tokens?.accessToken;

      if (!jwt) {
        console.error('No JWT token available for downloading plist');
        return;
      }

      const url = `${process.env.REACT_APP_USAGE_API}${
        process.env.REACT_APP_APP_CONFIG
      }?platform=Apple&app_id=${encodeURIComponent(appId)}`;

      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${jwt}` },
      });

      if (!res.ok) throw new Error(await res.text());
      // download it as a blob
      const blob = await res.blob();
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objUrl;
      a.download = `PresageService-Info.plist`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objUrl);
    } catch (err) {
      console.error("Failed to download plist:", err);
    }
  }

  async function downloadXml(appId) {
    try {
      // 1) grab Cognito access token
      const session = await fetchAuthSession();
      const jwt = session?.tokens?.accessToken;

      if (!jwt) {
        console.error('No JWT token available for downloading XML');
        return;
      }

      // 2) build your URL for Android
      const url = `${process.env.REACT_APP_USAGE_API}${
        process.env.REACT_APP_APP_CONFIG
      }?platform=Android&app_id=${encodeURIComponent(appId)}`;

      // 3) fetch as CORS
      const res = await fetch(url, {
        method: "GET",
        mode: "cors",
        headers: {
          Authorization: `Bearer ${jwt}`,
          "Content-Type": "application/xml",
        },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}: ${await res.text()}`);

      // 4) pull down the blob
      const blob = await res.blob();
      const downloadUrl = URL.createObjectURL(blob);

      // 5) infer filename from header or fallback
      const disposition = res.headers.get("Content-Disposition") || "";
      let filename = "presage_services.xml";
      if (disposition.includes("filename=")) {
        const parts = disposition.split("filename=");
        if (parts.length > 1) {
          filename = parts[1]
            .split(";")[0]
            .replace(/"/g, "")
            .trim();
        }
      }

      // 6) trigger download
      const a = document.createElement("a");
      a.href = downloadUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();

      // 7) cleanup
      URL.revokeObjectURL(downloadUrl);
    } catch (err) {
      console.error("Failed to download XML config:", err);
    }
  }

  async function fetchRegistrations() {
    setLoadingRegistrations(true);
    try {
      // 1. Get Cognito access token
      const session = await fetchAuthSession();
      const jwt = session?.tokens?.accessToken;

      if (!jwt) {
        console.error('No JWT token available for fetching registrations');
        setRegistrations([]);
        setShowRegisterForm(true);
        return;
      }

      // 2. Call your status endpoint
      const url = `${process.env.REACT_APP_USAGE_API}${process.env.REACT_APP_VERIFICATION_STATUS}`;
      const res = await fetch(url, {
        headers: {
          Authorization: `Bearer ${jwt}`,
        },
      });

      if (!res.ok) {
        console.error('Failed to fetch registrations:', res.status, await res.text());
        setRegistrations([]);
        setShowRegisterForm(true);
        return;
      }

      const json = await res.json();
      const { resultStatus, resultMessage } = json.response || {};

      let regs = [];

      // 3. On success, normalize resultMessage into an array of { app_id }
      if (
        res.ok &&
        resultStatus === "SUCCESS" &&
        resultMessage &&
        Array.isArray(resultMessage.app_ids)
      ) {
        regs = resultMessage.app_ids;
      }

      // 4. Update state: always set the array, and show form only if empty
      setRegistrations(regs);
      setShowRegisterForm(regs.length === 0);
    } catch (err) {
      console.error("Error fetching registrations:", err);
      setRegistrations([]);
      setShowRegisterForm(true);
    } finally {
      setLoadingRegistrations(false);
    }
  }

  function handlePlatformChange(e) {
    setPlatform(e.target.value);
    // clear the other field if you like:
    setAppIdInput("");
    setOrgIdInput("");
    setCertificateShaInput("");
    setAppShortDescription("");
    setApplicationType("");
    setUsageDomain("");
  }

  async function registerAppId() {
    setRegisterLoading(true);
    try {
      const session = await fetchAuthSession();
      const jwt = session?.tokens?.accessToken;

      if (!jwt) {
        console.error('No JWT token available for registering app');
        alert('Authentication error. Please try logging in again.');
        return;
      }

      const headers = {
        Authorization: `Bearer ${jwt}`,
        "Content-Type": "application/json",
      };

      const body = {
        platform,
        app_id: appIdInput,
        // include exactly one of the two:
        ...(platform === "Apple"
          ? { org_id: orgIdInput }
          : { certificate_sha: certificateShaInput }),
        // DevRM fields
        app_short_description: appShortDescription,
        application_type: applicationType,
        usage_domain: usageDomain,
      };

      const url = `${process.env.REACT_APP_USAGE_API}${process.env.REACT_APP_APP_ID}`;
      const res = await fetch(url, {
        method: "POST",
        headers,
        body: JSON.stringify(body),
      });
      
      if (!res.ok) {
        console.error('App registration failed:', res.status, await res.text());
        alert('Failed to register App ID. Please check your information and try again.');
        return;
      }
      
      const data = await res.json();

      if (data?.response?.resultStatus === "SUCCESS") {
        setShowRegisterForm(false);
        // Reset form
        setAppIdInput("");
        setOrgIdInput("");
        setCertificateShaInput("");
        setAppShortDescription("");
        setApplicationType("");
        setUsageDomain("");
        fetchRegistrations();
      } else {
        console.error("Register failed:", data);
        // Show error message from API directly to user:
        alert('Failed to register App ID. Please check your information and try again.');
      }
    } catch (error) {
      console.error("Error registering app ID:", error);
      alert('Error registering App ID. Please try again or contact support if the issue persists.');
    } finally {
      setRegisterLoading(false);
    }
  }

  // Add handler for enabling/disabling Sandbox mode
  async function handleEnableSandbox(entry) {
    // Determine the *new* value of sandbox after toggle
    const enabling = entry.sandbox !== true;
    const confirmed = window.confirm(
      enabling
        ? "Sandbox mode is for development use cases only and should be disabled in production. Please confirm you understand:"
        : "Disabling sandbox will prevent development apps from authenticating. Please confirm you want to disable sandbox mode."
    );
    if (!confirmed) return;

    try {
      const session = await fetchAuthSession();
      const jwt = session?.tokens?.accessToken;

      if (!jwt) {
        console.error('No JWT token available for updating sandbox mode');
        alert('Authentication error. Please try logging in again.');
        return;
      }

      // Confirmed usage of the consistent endpoint:
      const url = `${process.env.REACT_APP_USAGE_API}${process.env.REACT_APP_APP_ID}`;
      // Build the body object with required fields depending on platform
      const body = JSON.stringify({
        platform: entry.platform,
        app_id: entry.app_id,
        ...(entry.platform === "Apple" && entry.org_id
          ? { org_id: entry.org_id }
          : entry.platform === "Android" && entry.certificate_sha
          ? { certificate_sha: entry.certificate_sha }
          : {}),
        sandbox: entry.sandbox,
      });
      const res = await fetch(url, {
        method: "POST",
        mode: "cors",
        headers: {
          Authorization: `Bearer ${jwt}`,
          "Content-Type": "application/json",
        },
        body,
      });

      const json = await res.json();
      if (res.ok && json?.response?.resultStatus === "SUCCESS") {
        fetchRegistrations();
      } else {
        console.error("Failed to update sandbox mode:", json);
        alert('Failed to update sandbox mode. Please try again or contact support if the issue persists.');
      }
    } catch (err) {
      console.error("Error enabling sandbox mode:", err);
      alert('Error updating sandbox mode. Please try again.');
    }
  }

  return (
    <div className={styles["app-registration-container"]}>
      <div className={styles["app-registration-content"]}>
        <div className={styles["page-header"]}>
          <h1>App Registration for OAuth</h1>
          <p>Register your applications to enable OAuth authentication with the Presage API</p>
        </div>

        <div className={styles["registration-section"]}>
          {loadingRegistrations ? (
            // Loading state
            <div className={styles["loading-container"]}>
              <FaSpinner className={styles["spinner"]} />
              <p>Loading your registered applications...</p>
            </div>
          ) : registrations.length === 0 || showRegisterForm ? (
            // No registrations or showing form
            <div className={styles["register-form-container"]}>
              <div className={styles["form-header"]}>
                <h2>Register New Application</h2>
              </div>
              <p className={styles["subtitle"]} style={{ fontSize: "14px", marginBottom: "20px" }}>
                Learn how to register your app for OAuth{" "}
                <a
                  href="https://github.com/Presage-Security/SmartSpectra/blob/main/README.md"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: "#c91e1e",
                    textDecoration: "underline",
                  }}
                >
                  here
                </a>
              </p>

              {/* Platform Selection */}
              <div className={styles["platform-selection"]}>
                <h3>Platform</h3>
                <div className={styles["radio-group"]}>
                  <label className={styles["radio-label"]}>
                    <input
                      type="radio"
                      name="platform"
                      value="Apple"
                      checked={platform === "Apple"}
                      onChange={handlePlatformChange}
                    />
                    <span className={styles["radio-custom"]}></span>
                    Apple iOS
                  </label>
                  <label className={styles["radio-label"]}>
                    <input
                      type="radio"
                      name="platform"
                      value="Android"
                      checked={platform === "Android"}
                      onChange={handlePlatformChange}
                    />
                    <span className={styles["radio-custom"]}></span>
                    Android
                  </label>
                </div>
              </div>

              {/* App ID Field */}
              <div className={styles["field-group"]}>
                <label className={styles["field-label"]}>
                  Application ID <span className={styles["required-indicator"]}>*</span>
                </label>
                <input
                  required
                  className={styles["input-field"]}
                  type="text"
                  placeholder="com.example.app"
                  value={appIdInput}
                  onChange={(e) => setAppIdInput(e.target.value)}
                  onBlur={() => setAppIdInput(sanitizeAppId(appIdInput))}
                />
                <div className={styles["field-help"]}>
                  {platform === "Apple" && (
                    <span>
                      Your Bundle ID.{" "}
                      <a
                        href="https://developer.apple.com/documentation/appstoreconnectapi/bundle-ids"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Learn more
                      </a>
                    </span>
                  )}
                  {platform === "Android" && (
                    <span>
                      Your Application ID (package name).{" "}
                      <a
                        href="https://developer.android.com/studio/build/application-id"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Learn more
                      </a>
                    </span>
                  )}
                </div>
              </div>

              {/* Platform-specific fields */}
              {platform === "Apple" ? (
                <div className={styles["field-group"]}>
                  <label className={styles["field-label"]}>
                    Organization ID <span className={styles["required-indicator"]}>*</span>
                  </label>
                  <input
                    required
                    className={styles["input-field"]}
                    type="text"
                    placeholder="e.g. 12345ABCDE"
                    value={orgIdInput}
                    onChange={(e) => setOrgIdInput(e.target.value)}
                    onBlur={() => setOrgIdInput(sanitizeOrgId(orgIdInput))}
                  />
                  <div className={styles["field-help"]}>
                    Your Apple Developer Organization ID.{" "}
                    <a
                      href="https://developer.apple.com/help/account/manage-your-team/locate-your-team-id/"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Learn more
                    </a>
                  </div>
                </div>
              ) : (
                <div className={styles["field-group"]}>
                  <label className={styles["field-label"]}>
                    Certificate SHA256 Fingerprint <span className={styles["required-indicator"]}>*</span>
                  </label>
                  <input
                    required
                    className={styles["input-field"]}
                    type="text"
                    placeholder="Certificate SHA256 Fingerprint"
                    value={certificateShaInput}
                    onChange={(e) => setCertificateShaInput(e.target.value)}
                    onBlur={() =>
                      setCertificateShaInput(sanitizeSha(certificateShaInput))
                    }
                  />
                  <div className={styles["field-help"]}>
                    Your Android signing certificate SHA-256 fingerprint.{" "}
                    <a
                      href="https://developers.google.com/android/guides/client-auth"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Learn more
                    </a>
                  </div>
                </div>
              )}

              {/* DevRM fields */}
              <div className={styles["devRM-section"]}>
                <h3>Application Details</h3>
                <div className={styles["devRM-fields"]}>
                  <div className={styles["field-group"]}>
                    <label className={styles["field-label"]}>
                      App Description <span className={styles["required-indicator"]}>*</span>
                    </label>
                    <input
                      required
                      className={styles["input-field"]}
                      type="text"
                      placeholder="Brief description of your app"
                      value={appShortDescription}
                      onChange={(e) => setAppShortDescription(e.target.value)}
                    />
                  </div>

                  <div className={styles["field-group"]}>
                    <label className={styles["field-label"]}>
                      Application Type <span className={styles["required-indicator"]}>*</span>
                    </label>
                    <select
                      required
                      className={styles["input-field"]}
                      value={applicationType}
                      onChange={(e) => setApplicationType(e.target.value)}
                    >
                      <option value="">Select application type</option>
                      <option value="Monitoring">Monitoring</option>
                      <option value="Therapy">Therapy</option>
                      <option value="Diagnosis">Diagnosis</option>
                      <option value="Treatment">Treatment</option>
                      <option value="Security">Security</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  <div className={styles["field-group"]}>
                    <label className={styles["field-label"]}>
                      Usage Domain <span className={styles["required-indicator"]}>*</span>
                    </label>
                    <select
                      required
                      className={styles["input-field"]}
                      value={usageDomain}
                      onChange={(e) => setUsageDomain(e.target.value)}
                    >
                      <option value="">Select usage domain</option>
                      <option value="Outpatient">Outpatient</option>
                      <option value="Inpatient">Inpatient</option>
                      <option value="Gaming">Gaming</option>
                      <option value="Sports/Performance">Sports/Performance</option>
                      <option value="Wellness">Wellness</option>
                      <option value="Space">Space</option>
                      <option value="Military">Military</option>
                      <option value="Intel">Intel</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className={styles["form-actions"]}>
                <button
                  onClick={registerAppId}
                  disabled={
                    registerLoading ||
                    !appIdInput ||
                    !appShortDescription ||
                    !applicationType ||
                    !usageDomain ||
                    (platform === "Apple" ? !orgIdInput : !certificateShaInput)
                  }
                  className={`btn btn-primary ${styles["register-btn"]}`}
                >
                  {registerLoading ? (
                    <>
                      <FaSpinner className={styles["spinner"]} />
                      Registering...
                    </>
                  ) : (
                    "Register Application"
                  )}
                </button>
                {registrations.length !== 0 && showRegisterForm && (
                  <button
                    onClick={() => setShowRegisterForm(false)}
                    className={`btn btn-secondary ${styles["cancel-btn"]}`}
                  >
                    Cancel
                  </button>
                )}
              </div>
            </div>
          ) : (
            // Has registrations and not showing form
            <div className={styles["registrations-list"]}>
              <div className={styles["list-header"]}>
                <h2>Your Registered Applications</h2>
              </div>
              <p className={styles["subtitle"]} style={{ fontSize: "14px", marginBottom: "20px" }}>
                Learn how to use OAuth in your app{" "}
                <a
                  href="https://github.com/Presage-Security/SmartSpectra/blob/main/README.md"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: "#c91e1e",
                    textDecoration: "underline",
                  }}
                >
                  here
                </a>
              </p>

              <div className={styles["apps-table-container"]}>
                <table className={styles["apps-table"]}>
                  <thead>
                    <tr>
                      <th>App ID</th>
                      <th>Platform</th>
                      <th>Sandbox Status</th>
                      <th>Download Config</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {registrations.map((r) => (
                      <tr key={r.id}>
                        <td>{r.app_id}</td>
                        <td>{r.platform}</td>
                        <td>
                          <div className={styles["sandbox-cell"]}>
                            <span className={r.sandbox ? styles["enabled"] : styles["disabled"]}>
                              {r.sandbox ? "Enabled" : "Disabled"}
                            </span>
                            <button
                              onClick={() => handleEnableSandbox({ ...r, sandbox: !r.sandbox })}
                              className={`btn btn-secondary btn-sm ${styles["toggle-btn"]}`}
                            >
                              {r.sandbox ? "Disable" : "Enable"}
                            </button>
                          </div>
                        </td>
                        <td>
                          <div className={styles["download-actions"]}>
                            {r.org_id && (
                              <button
                                onClick={() => downloadPlist(r.app_id)}
                                className={`btn btn-secondary btn-sm ${styles["download-btn"]}`}
                              >
                                Download .plist
                              </button>
                            )}
                            {r.certificate_sha && (
                              <button
                                onClick={() => downloadXml(r.app_id)}
                                className={`btn btn-secondary btn-sm ${styles["download-btn"]}`}
                              >
                                Download .xml
                              </button>
                            )}
                          </div>
                        </td>
                        <td>
                          <button
                            onClick={() => deleteAppId(r.id)}
                            className={`btn btn-danger btn-sm ${styles["delete-btn"]}`}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className={styles["add-app-container"]}>
                <button
                  onClick={() => setShowRegisterForm(true)}
                  className={`btn btn-primary ${styles["add-app-btn"]}`}
                >
                  Register Another Application
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AppRegistration;
